<template>
    <div class="row">
        <div class="col-12">
            <div class="form-group">
                <div class="row justify-content-center">
                    <div
                        v-for="productOption in options"
                        :key="productOption.id"
                        class="col-12 col-md-6 mb-2"
                        :class="{
                            'text-md-right': 0 === getIndexForVignetteOption(productOption.id),
                        }"
                    >
                        <div
                            class="checkbox-button-container"
                            @change="productOptionSelection(productOption)"
                            @click="productOptionSelection(productOption)"
                        >
                            <input
                                type="radio"
                                class="checkbox"
                                name="option_id"
                                :value="productOption.id"
                                :id="'vignette_product_' + productOption.id"
                                :class="{ 'is-invalid': formErrors.product_id }"
                                :price="productOption.price.toFixed(2)"
                                required
                            />
                            <label
                                :for="'vignette_option_' + productOption.id"
                                class="mb-0 shop-product-card alternative bg-white px-4 py-2 rounded text-center text-reset-case"
                                :class="{
                                    highlighted: productOption.id === selectedProductOption,
                                }"
                            >
                                <i class="mdi mdi-road-variant h5 mr-1"></i>
                                <h2 class="title mt-2 mb-0">
                                    {{ productOption.product.name }}
                                </h2>
                                <small class="mt-1 mb-0">
                                    {{ productOption.product.description }}
                                </small>
                                <price class="h1 mt-2" :price="getProductOptionPrice(productOption)"></price>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-12" v-show="productOptionSelected && showDatePicker">
            <div class="form-group mb-2">
                <label class="col-form-label" for="valid_from">
                    {{ $t('shop.validity_start') }}
                </label>
                <div class="datepicker-overlay">
                    <DatePicker
                        id="valid_from"
                        name="validity_start"
                        required="required"
                        :config="datePickerPreset"
                        :mobileIcon="true"
                        @dp-change="
                            $emit('validity-start-change', vignetteValidFrom);
                            $emit('display-type-options');
                            $emit('input-change', 'validity_start');
                        "
                        @close="validityFromNotice = null"
                        :class="{ 'is-invalid': formErrors.validity_start }"
                        v-model="vignetteValidFrom"
                        ref="validFrom"
                        @dp-invalid-date="$emit('set-invalid-valid-from-message', $event);"
                        :disable-placeholder="setVignetteValidFromNullSetting"
                    >
                    </DatePicker>
                    <div class="invalid-feedback">
                        <div v-if="formErrors.validity_start && !vignetteValidFrom">
                            {{ $t('shop.validity_from_input_empty_error') }}
                        </div>
                        <div v-else>
                            <span class="d-block" v-for="error in formErrors.validity_start" :key="error">
                                {{ error }}
                            </span>
                        </div>
                    </div>
                    <div class="invalid-validity-start" v-if="validityFromNotice">
                        <span class="d-block" v-html="validityFromNotice"></span>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="validToDate && Object.keys(validToDate).length > 0 && productOptionSelected"
            class="col-12 text-center"
        >
            <div class="vignette-validity-until mt-0 mb-4 h5 bg-light rounded py-2">
                <span class="prefix-label">{{ $t('shop.validityPrefix') }}</span>
                <span class="from-label">{{ vignetteValidFrom }}</span>
                <span class="separator-label">{{ $t('shop.validitySeparator') }}</span>
                <span class="to-date-label">{{ validToDate.date }}</span>
                <span class="to-time-label">{{ validToDate.time }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from '../../DatePicker.vue';

export default {
    name: 'ProductOption',
    components: { DatePicker },

    props: [
        'datePickerOptions',
        'formErrors',
        'validFromNotice',
        'validTo',
        'validFrom',
        'options',
        'selectedOption',
        'defaultSelectedOption',
        'showDatePicker',
        'setValidFromNull',
    ],

    data() {
        return {
            validityFromNotice: this.validFromNotice,
            vignetteValidFrom: this.setValidFromNull ? null : this.validFrom.date,
            selectedVignetteOption: this.selectedOption,
            datePickerPreset: this.datePickerOptions,
            selectedProductOption: this.selectedOption,
            productOptionSelected: false,
            setVignetteValidFromNullSetting: this.setValidFromNull,
            validToDate: this.setValidFromNull ? null : this.validTo,

        };
    },

    watch: {
        vignetteValidFrom() {
            if (this.setVignetteValidFromNullSetting && this.selectedProductOption.is_validity_controllable) {
                this.vignetteValidFrom = null;
                this.validToDate = null;
            }

            this.formErrors.validity_start = null;
        },

        validTo() {
            this.validToDate = this.validTo;
        },
    },

    methods: {
        updateField(field, value) {
            this.$emit('field-updated', {field: field, value: value});
        },

        getIndexForVignetteOption(id) {
            id = '' + id;

            const ids = Object.keys(this.options);
            for (let i = 0; i < ids.length; i++) {
                if (ids[i] === id) {
                    return i;
                }
            }

            return -1;
        },

        getProductOptionPrice(productOption) {
            return productOption.price + (productOption.service_fee || 0);
        },

        updateStructuredProductData(product) {
            const structuredDataStore = document.querySelector('#structured-product');

            if (structuredDataStore === null) {
                return;
            }

            if (product === null || typeof(product.metadata) !== 'object') {
                structuredDataStore.innerText = '';
                return;
            }

            // Replace the description content if available at the product.
            const description = document.querySelector('#description');
            description.innerHTML = product.metadata.shopDescription ?? '';

            structuredDataStore.innerText = JSON.stringify(product.metadata.structuredData);
        },

        productOptionSelection(productOption) {
            this.selectedProductOption = productOption.id;

            this.$emit('show-loading-animation');
            this.$emit('input-change');
            this.$emit('product-option-selected', productOption);
            this.productOptionSelected = true;

            document.getElementById('product-name').innerText = productOption.product.name;

            if (typeof productOption.product.enable_subscription !== 'undefined') {
                this.updateField('paymentSubscription', productOption.enable_subscription);
            }

            setTimeout(() => {
                this.updateStructuredProductData(productOption.product);

                let scrollSelector = '[for="valid_from"]';
                if (!this.showDatePicker) {
                    scrollSelector = '#orderContainer';
                }

                const elementY = document.querySelector(scrollSelector).getBoundingClientRect().top;
                const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                const windowY = window.scrollY;

                window.scrollTo(0, windowY + elementY - topNavHeight);
            }, 75);
        },
    },
};
</script>
